/* ------------------------------------------------------------------
   /frontend/src/pages/dashboard/post/youtube/YouTubePostService.js
   v2.0 - Upgraded with comprehensive, user-friendly error handling.
------------------------------------------------------------------- */
import axios from 'axios';

/**
 * publishYouTubePost
 * @param {string}        youtubeId
 * @param {string}        caption
 * @param {Array<Object>} files
 * @param {Object}        opts – pulled from drawer (title, visibility, madeForKids, forceShort, tags)
 * @returns {Promise<object>} The JSON response from your backend on success.
 * @throws {Error}            Throws a new Error with a user-friendly message on failure.
 */
export async function publishYouTubePost(youtubeId, caption, files = [], opts = {}) {
  const body = new FormData();
  body.append('youtubeId', youtubeId);

  /* prefer per-account Title from drawer; fall back to caption */
  body.append('title', opts.title?.trim() || caption || 'GreatlySocial upload');
  body.append('description', caption || '');

  /* media */
  if (files.length) {
    const first = files[0];
    if (first.file) {
      body.append('file', first.file);
    } else {
      body.append('fileUrl', first.url);
    }
  }

  /* drawer opts */
  body.append('visibility',  opts.visibility  || 'public');
  body.append('madeForKids', opts.madeForKids ? '1' : '0');
  body.append('forceShort',  opts.forceShort  ? '1' : '0');
  body.append('tags',        JSON.stringify(opts.tags ?? []));

  try {
    const { data } = await axios.post('/api/posting/youtube/publish', body, {
      withCredentials: true,
    });
    return data;
  } catch (err) {
    console.error('[YouTube Web Service] Error:', err.response ? err.response.data : err.message);
    
    // --- START: Comprehensive Error Parsing Logic ---
    let errorMessage = 'Failed to post to YouTube.';
    
    if (!err.response && err.message) {
      // Handle client-side validation errors
      errorMessage = err.message;
    } else if (err.response?.data) {
      const errorData = err.response.data;
      
      // Check for specific error messages from your backend first
      if (typeof errorData.error === 'string') {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } 
      // Check for detailed error objects from the Google API
      else if (errorData.error?.message) {
        errorMessage = errorData.error.message;
        // Sometimes Google nests errors further
        if (errorData.error.errors && Array.isArray(errorData.error.errors) && errorData.error.errors[0]) {
          const googleError = errorData.error.errors[0];
          const reason = googleError.reason;
          switch(reason) {
            case 'youtubeSignupRequired':
              errorMessage = 'The selected account does not have a YouTube channel. Please create one on YouTube.';
              break;
            case 'forbidden':
              errorMessage = 'Access denied. Please check your account permissions or reconnect your YouTube account.';
              break;
            case 'videoNotFound':
              errorMessage = 'The video could not be processed by YouTube.';
              break;
            case 'uploadLimitExceeded':
            case 'userRateLimitExceeded':
            case 'quotaExceeded':
              errorMessage = 'YouTube daily upload limit reached. Please try again tomorrow.';
              break;
            case 'invalidVideo':
              errorMessage = 'Invalid video format. Please use a supported format like MP4 or MOV.';
              break;
            default:
              errorMessage = googleError.message || errorMessage;
          }
        }
      } else if (typeof errorData === 'string') {
        errorMessage = errorData;
      }
    } else if (err.response?.status) {
      // Fallback for generic HTTP status codes
      switch (err.response.status) {
        case 400:
          errorMessage = 'Invalid request. Please check your video file and post details.';
          break;
        case 401:
          errorMessage = 'Authentication failed. Please reconnect your YouTube account.';
          break;
        case 403:
          errorMessage = 'Permission denied. Your account may not have permission to upload videos.';
          break;
        case 500:
        case 503:
          errorMessage = 'A server error occurred on YouTube. Please try again later.';
          break;
        default:
          errorMessage = `An unexpected error occurred (Status: ${err.response.status}).`;
      }
    } else if (err.message) {
      // Fallback for network errors etc.
      errorMessage = err.message;
    }
    // --- END: Comprehensive Error Parsing Logic ---

    // Throw a new error with the clean, user-friendly message.
    throw new Error(errorMessage);
  }
}
